import React, { useEffect, useState } from "react";
import "./Step4Nominee.css";

/* ==========================================================
   INITIAL FORM DATA
========================================================== */

const getInitialData = (formData = {}) => ({
  /* ========================================================
     NOMINEE DETAILS
  ======================================================== */

  nomineeName:
    formData?.nomineeName || "",

  nomineeRelationship:
    formData?.nomineeRelationship ||
    formData?.relationship ||
    "",

  nomineeDateOfBirth:
    formData?.nomineeDateOfBirth ||
    formData?.nomineeDob ||
    "",

  nomineeMobileNumber:
    formData?.nomineeMobileNumber ||
    formData?.nomineeMobile ||
    "",

  /* ========================================================
     BANK DETAILS - OPTIONAL
  ======================================================== */

  accountHolderName:
    formData?.accountHolderName || "",

  bankName:
    formData?.bankName || "",

  accountNumber:
    formData?.accountNumber || "",

  ifscCode:
    formData?.ifscCode || "",

  bankBranch:
    formData?.bankBranch || "",

  accountType:
    formData?.accountType || "",
});


/* ==========================================================
   STEP 4 COMPONENT
========================================================== */

function Step4Nominee({
  onNext,
  onBack,
  formData = {},
  updateFormData,
}) {

  /* ========================================================
     STATE
  ======================================================== */

  const [data, setData] = useState(() =>
    getInitialData(formData)
  );

  const [errors, setErrors] = useState({});


  /* ========================================================
     SYNC WITH PARENT DATA
  ======================================================== */

  useEffect(() => {
    setData(getInitialData(formData));
    setErrors({});
  }, [formData]);


  /* ==========================================================
     CHARACTER + SPACE ONLY

     Used for:
     - Nominee Name
     - Account Holder Name
     - Bank Branch
  ========================================================== */

  const handleNameOnlyChange = (event) => {

    const { name, value } = event.target;

    const cleanedValue = value
      .replace(/[^A-Za-z\s]/g, "")
      .replace(/\s{2,}/g, " ");

    setData((previousData) => ({
      ...previousData,
      [name]: cleanedValue,
    }));

    setErrors((previousErrors) => ({
      ...previousErrors,
      [name]: "",
    }));
  };


  /* ==========================================================
     NORMAL INPUT CHANGE
  ========================================================== */

  const handleChange = (event) => {

    const { name, value } = event.target;

    setData((previousData) => ({
      ...previousData,
      [name]: value,
    }));

    setErrors((previousErrors) => ({
      ...previousErrors,
      [name]: "",
    }));
  };


  /* ==========================================================
     NOMINEE MOBILE NUMBER

     ONLY 10 DIGITS
  ========================================================== */

  const handleMobileChange = (event) => {

    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 10);

    setData((previousData) => ({
      ...previousData,
      nomineeMobileNumber: value,
    }));

    setErrors((previousErrors) => ({
      ...previousErrors,
      nomineeMobileNumber: "",
    }));
  };


  /* ==========================================================
     ACCOUNT NUMBER

     OPTIONAL
     IF ENTERED:
     - ONLY NUMBERS
     - MAXIMUM 12 DIGITS
  ========================================================== */

  const handleAccountNumberChange = (event) => {

    const value = event.target.value
      .replace(/[^0-9]/g, "")
      .slice(0, 12);

    setData((previousData) => ({
      ...previousData,
      accountNumber: value,
    }));

    setErrors((previousErrors) => ({
      ...previousErrors,
      accountNumber: "",
    }));
  };


  /* ==========================================================
     IFSC CODE

     OPTIONAL
     IF ENTERED:
     - ONLY A-Z
     - ONLY 0-9
     - AUTOMATIC UPPERCASE
     - MAXIMUM 11 CHARACTERS
  ========================================================== */

  const handleIfscChange = (event) => {

    const value = event.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 11);

    setData((previousData) => ({
      ...previousData,
      ifscCode: value,
    }));

    setErrors((previousErrors) => ({
      ...previousErrors,
      ifscCode: "",
    }));
  };


  /* ==========================================================
     VALIDATE FORM
  ========================================================== */

  const validateForm = () => {

    const newErrors = {};


    /* ========================================================
       NOMINEE FULL NAME
       REQUIRED
    ======================================================== */

    if (!data.nomineeName.trim()) {

      newErrors.nomineeName =
        "Please enter nominee full name.";

    } else if (
      !/^[A-Za-z]+(?:\s+[A-Za-z]+)*$/.test(
        data.nomineeName.trim()
      )
    ) {

      newErrors.nomineeName =
        "Name should contain only characters and spaces.";
    }


    /* ========================================================
       RELATIONSHIP
       REQUIRED
    ======================================================== */

    if (!data.nomineeRelationship) {

      newErrors.nomineeRelationship =
        "Please select relationship.";
    }


    /* ========================================================
       DATE OF BIRTH
       REQUIRED
    ======================================================== */

    if (!data.nomineeDateOfBirth) {

      newErrors.nomineeDateOfBirth =
        "Please select nominee date of birth.";
    }


    /* ========================================================
       MOBILE NUMBER
       REQUIRED
    ======================================================== */

    if (!data.nomineeMobileNumber.trim()) {

      newErrors.nomineeMobileNumber =
        "Please enter nominee mobile number.";

    } else if (
      !/^[6-9][0-9]{9}$/.test(
        data.nomineeMobileNumber
      )
    ) {

      newErrors.nomineeMobileNumber =
        "Please enter a valid 10-digit mobile number.";
    }


    /* ========================================================
       OPTIONAL BANK DETAILS

       Bank section is optional.

       Validation happens ONLY if the user enters
       something into a field.
    ======================================================== */


    /* --------------------------------------------------------
       ACCOUNT HOLDER NAME
    -------------------------------------------------------- */

    if (data.accountHolderName.trim()) {

      if (
        !/^[A-Za-z]+(?:\s+[A-Za-z]+)*$/.test(
          data.accountHolderName.trim()
        )
      ) {

        newErrors.accountHolderName =
          "Account holder name should contain only characters and spaces.";
      }
    }


    /* --------------------------------------------------------
       BANK NAME
    -------------------------------------------------------- */

    // Bank name is optional.
    // No validation required when empty.


    /* --------------------------------------------------------
       ACCOUNT NUMBER
    -------------------------------------------------------- */

    if (data.accountNumber.trim()) {

      if (
        !/^[0-9]{12}$/.test(
          data.accountNumber
        )
      ) {

        newErrors.accountNumber =
          "Account number must contain exactly 12 digits.";
      }
    }


    /* --------------------------------------------------------
       IFSC CODE
    -------------------------------------------------------- */

    if (data.ifscCode.trim()) {

      if (
        !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(
          data.ifscCode.toUpperCase()
        )
      ) {

        newErrors.ifscCode =
          "Please enter a valid 11-character IFSC code.";
      }
    }


    /* --------------------------------------------------------
       BANK BRANCH
    -------------------------------------------------------- */

    if (data.bankBranch.trim()) {

      if (
        !/^[A-Za-z]+(?:\s+[A-Za-z]+)*$/.test(
          data.bankBranch.trim()
        )
      ) {

        newErrors.bankBranch =
          "Bank branch should contain only characters and spaces.";
      }
    }


    /* --------------------------------------------------------
       ACCOUNT TYPE

       OPTIONAL - NO VALIDATION
    -------------------------------------------------------- */


    return newErrors;
  };


  /* ==========================================================
     NEXT BUTTON
  ========================================================== */

  const handleNext = (event) => {

    event.preventDefault();

    const newErrors = validateForm();

    setErrors(newErrors);


    /* --------------------------------------------------------
       STOP IF ERRORS
    -------------------------------------------------------- */

    if (Object.keys(newErrors).length > 0) {
      return;
    }


    /* --------------------------------------------------------
       CLEAN FINAL DATA
    -------------------------------------------------------- */

    const updatedData = {

      ...data,

      /* NOMINEE */

      nomineeName:
        data.nomineeName.trim(),

      nomineeRelationship:
        data.nomineeRelationship,

      nomineeDateOfBirth:
        data.nomineeDateOfBirth,

      nomineeMobileNumber:
        data.nomineeMobileNumber,


      /* OPTIONAL BANK DETAILS */

      accountHolderName:
        data.accountHolderName.trim(),

      bankName:
        data.bankName,

      accountNumber:
        data.accountNumber,

      ifscCode:
        data.ifscCode.toUpperCase(),

      bankBranch:
        data.bankBranch.trim(),

      accountType:
        data.accountType,
    };


    /* --------------------------------------------------------
       DEBUG
    -------------------------------------------------------- */

    console.log(
      "STEP 4 - FINAL DATA:",
      updatedData
    );


    /* --------------------------------------------------------
       UPDATE PARENT
    -------------------------------------------------------- */

    if (
      typeof updateFormData === "function"
    ) {

      updateFormData(updatedData);
    }


    /* --------------------------------------------------------
       SAVE TO LOCAL STORAGE
    -------------------------------------------------------- */

    const completeRegistrationData = {

      ...formData,

      ...updatedData,
    };


    localStorage.setItem(
      "registrationData",
      JSON.stringify(
        completeRegistrationData
      )
    );


    localStorage.setItem(
      "registrationNominee",
      JSON.stringify(
        updatedData
      )
    );


    /* --------------------------------------------------------
       GO TO NEXT STEP
    -------------------------------------------------------- */

    if (
      typeof onNext === "function"
    ) {

      onNext(updatedData);
    }
  };


  /* ==========================================================
     BACK BUTTON
  ========================================================== */

  const handleBack = () => {

    if (
      typeof onBack === "function"
    ) {

      onBack();
    }
  };


  /* ==========================================================
     ERROR DISPLAY
  ========================================================== */

  const renderError = (fieldName) => {

    if (!errors[fieldName]) {
      return null;
    }

    return (
      <div className="step4-error">
        {errors[fieldName]}
      </div>
    );
  };


  /* ==========================================================
     RENDER
  ========================================================== */

  return (

    <div className="step4-page">

      <div className="step4-card">

        <form onSubmit={handleNext}>

          {/* ==================================================
              A. NOMINEE DETAILS
          ================================================== */}

          <div className="step4-section-title">
            A. Nominee Details
          </div>


          <div className="step4-form-grid">

            {/* ==================================================
                NOMINEE FULL NAME
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Nominee Full Name
                <span>*</span>
              </label>

              <input
                type="text"
                name="nomineeName"
                value={data.nomineeName}
                onChange={handleNameOnlyChange}
                placeholder="Enter nominee full name"
                autoComplete="off"
              />

              {renderError("nomineeName")}

            </div>


            {/* ==================================================
                RELATIONSHIP
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Relationship with Member
                <span>*</span>
              </label>

              <select
                name="nomineeRelationship"
                value={
                  data.nomineeRelationship
                }
                onChange={handleChange}
              >

                <option value="">
                  Select Relationship
                </option>

                <option value="Father">
                  Father
                </option>

                <option value="Mother">
                  Mother
                </option>

                <option value="Wife">
                  Wife
                </option>

                <option value="Husband">
                  Husband
                </option>

                <option value="Son">
                  Son
                </option>

                <option value="Daughter">
                  Daughter
                </option>

                <option value="Brother">
                  Brother
                </option>

                <option value="Sister">
                  Sister
                </option>

                <option value="Other">
                  Other
                </option>

              </select>

              {renderError(
                "nomineeRelationship"
              )}

            </div>


            {/* ==================================================
                DATE OF BIRTH
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Date of Birth
                <span>*</span>
              </label>

              <input
                type="date"
                name="nomineeDateOfBirth"
                value={
                  data.nomineeDateOfBirth
                }
                onChange={handleChange}
              />

              {renderError(
                "nomineeDateOfBirth"
              )}

            </div>


            {/* ==================================================
                MOBILE NUMBER
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Mobile Number
                <span>*</span>
              </label>

              <div className="step4-mobile-input">

                <div className="step4-country-code">
                  +91
                </div>

                <input
                  type="tel"
                  name="nomineeMobileNumber"
                  value={
                    data.nomineeMobileNumber
                  }
                  onChange={handleMobileChange}
                  placeholder="Enter mobile number"
                  maxLength={10}
                  inputMode="numeric"
                  autoComplete="tel"
                />

              </div>

              {renderError(
                "nomineeMobileNumber"
              )}

            </div>

          </div>


          {/* ==================================================
              DIVIDER
          ================================================== */}

          <div className="step4-divider"></div>


          {/* ==================================================
              B. BANK ACCOUNT DETAILS - OPTIONAL
          ================================================== */}

          <div className="step4-section-title">
            B. Bank Account Details
            <span className="step4-optional-label">
              (Optional)
            </span>
          </div>


          <div className="step4-form-grid">

            {/* ==================================================
                ACCOUNT HOLDER NAME
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Account Holder Name
               
              </label>

              <input
                type="text"
                name="accountHolderName"
                value={
                  data.accountHolderName
                }
                onChange={handleNameOnlyChange}
                placeholder="Enter account holder name"
                autoComplete="name"
              />

              {renderError(
                "accountHolderName"
              )}

            </div>


            {/* ==================================================
                BANK NAME
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Bank Name
                
              </label>

              <select
                name="bankName"
                value={data.bankName}
                onChange={handleChange}
              >

                <option value="">
                  Select Bank
                </option>

                <option value="State Bank of India">
                  State Bank of India
                </option>

                <option value="HDFC Bank">
                  HDFC Bank
                </option>

                <option value="ICICI Bank">
                  ICICI Bank
                </option>

                <option value="Axis Bank">
                  Axis Bank
                </option>

                <option value="Bank of Baroda">
                  Bank of Baroda
                </option>

                <option value="Canara Bank">
                  Canara Bank
                </option>

                <option value="Union Bank of India">
                  Union Bank of India
                </option>

                <option value="Indian Bank">
                  Indian Bank
                </option>

                <option value="Other">
                  Other
                </option>

              </select>

              {renderError(
                "bankName"
              )}

            </div>


            {/* ==================================================
                ACCOUNT NUMBER
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Account Number
              
              </label>

              <input
                type="text"
                name="accountNumber"
                value={
                  data.accountNumber
                }
                onChange={
                  handleAccountNumberChange
                }
                placeholder="Enter 12-digit account number"
                maxLength={12}
                inputMode="numeric"
                autoComplete="off"
              />

              {renderError(
                "accountNumber"
              )}

            </div>


            {/* ==================================================
                IFSC CODE
            ================================================== */}

            <div className="step4-form-group">

              <label>
                IFSC Code
                
              </label>

              <input
                type="text"
                name="ifscCode"
                value={
                  data.ifscCode
                }
                onChange={
                  handleIfscChange
                }
                placeholder="Enter 11-character IFSC code"
                maxLength={11}
                autoComplete="off"
                spellCheck="false"
              />

              {renderError(
                "ifscCode"
              )}

            </div>


            {/* ==================================================
                BANK BRANCH
            ================================================== */}

            <div className="step4-form-group">

              <label>
                Bank Branch
                
              </label>

              <input
                type="text"
                name="bankBranch"
                value={
                  data.bankBranch
                }
                onChange={
                  handleNameOnlyChange
                }
                placeholder="Enter bank branch"
                autoComplete="off"
              />

              {renderError(
                "bankBranch"
              )}

            </div>


            {/* ==================================================
                ACCOUNT TYPE - OPTIONAL
            ================================================== */}

           

          </div>


          {/* ==================================================
              ACTION BUTTONS
          ================================================== */}

          <div className="step4-actions">

            <button
              type="button"
              className="step4-button step4-back-button"
              onClick={handleBack}
            >
              BACK
            </button>


            <button
              type="submit"
              className="step4-button step4-next-button"
            >
              NEXT
            </button>

          </div>

        </form>

      </div>

    </div>
  );
}

export default Step4Nominee;